import { useState, useEffect, useRef, useCallback } from 'react'
import { Bell, LogOut, User, Phone, Check, ChevronDown } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../../stores/auth'
import { useUIStore } from '../../stores/ui'
import { notificationsApi, teamsApi } from '../../api/endpoints'
import { api } from '../../api/client'

type AvailabilityStatus = 'online' | 'offline' | 'break'

const STATUS_CONFIG: Record<AvailabilityStatus, { color: string; bg: string; label: string }> = {
  online: { color: 'bg-green-500', bg: 'bg-green-50 text-green-700', label: 'Online' },
  offline: { color: 'bg-gray-400', bg: 'bg-gray-100 text-gray-600', label: 'Offline' },
  break: { color: 'bg-yellow-400', bg: 'bg-yellow-50 text-yellow-700', label: 'Break' },
}

function AvailabilityToggle({ userId }: { userId: number }) {
  const [status, setStatus] = useState<AvailabilityStatus | null>(null)
  const [teamIds, setTeamIds] = useState<number[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const ref = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Fetch teams on mount to find the user's membership and current availability
  useEffect(() => {
    let cancelled = false
    async function fetchTeams() {
      try {
        const res = await teamsApi.list()
        const teams = res.teams || []
        const myTeamIds: number[] = []
        let currentStatus: AvailabilityStatus | null = null

        for (const team of teams) {
          // Fetch members for each team to check if user belongs
          const detail = await teamsApi.get(team.id)
          const members = detail.data?.members || []
          const me = members.find((m) => m.user_id === userId && m.is_active)
          if (me) {
            myTeamIds.push(team.id)
            if (!currentStatus) {
              currentStatus = (me.availability as AvailabilityStatus) || 'offline'
            }
          }
        }

        if (!cancelled) {
          setTeamIds(myTeamIds)
          setStatus(currentStatus)
          setLoading(false)
        }
      } catch {
        if (!cancelled) setLoading(false)
      }
    }
    fetchTeams()
    return () => { cancelled = true }
  }, [userId])

  const changeStatus = useCallback(async (newStatus: AvailabilityStatus) => {
    const prev = status
    setStatus(newStatus) // optimistic
    setOpen(false)

    try {
      await Promise.all(teamIds.map((tid) => teamsApi.setAvailability(tid, newStatus)))
    } catch {
      setStatus(prev) // revert on failure
    }
  }, [status, teamIds])

  // Don't render if still loading or user isn't on any team
  if (loading || teamIds.length === 0 || !status) return null

  const cfg = STATUS_CONFIG[status]

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} hover:opacity-80 transition-opacity`}
      >
        <span className={`w-2 h-2 rounded-full ${cfg.color}`} />
        <span className="hidden sm:inline">{cfg.label}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-9 w-36 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
          {(Object.entries(STATUS_CONFIG) as [AvailabilityStatus, typeof cfg][]).map(([key, val]) => (
            <button
              key={key}
              onClick={() => changeStatus(key)}
              className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-gray-50 ${
                key === status ? 'font-semibold' : ''
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${val.color}`} />
              {val.label}
              {key === status && <Check size={12} className="ml-auto text-indigo-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function Header() {
  const { user, logout } = useAuthStore()
  const sidebarOpen = useUIStore((s) => s.sidebarOpen)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [editingPhone, setEditingPhone] = useState(false)
  const [myPhone, setMyPhone] = useState('')
  const [phoneSaved, setPhoneSaved] = useState(false)

  // Load user's phone on mount
  useEffect(() => {
    if (user) {
      api.get<{ settings: Record<string, string> }>('/settings')
        .then(data => {
          const phone = data.settings?.[`user_${user.id}_phone`] || ''
          setMyPhone(phone)
        })
        .catch(() => {})
    }
  }, [user])

  const savePhone = async () => {
    if (!user) return
    await api.post('/settings/batch', { settings: { [`user_${user.id}_phone`]: myPhone } })
    setEditingPhone(false)
    setPhoneSaved(true)
    setTimeout(() => setPhoneSaved(false), 2000)
  }

  const { data: notifCount } = useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: notificationsApi.count,
    refetchInterval: 30000,
  })

  const unread = notifCount?.unread_count || 0

  return (
    <header
      className={`fixed top-0 right-0 h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 md:px-6 z-20 transition-all duration-200 left-0 ${
        sidebarOpen ? 'md:left-56' : 'md:left-16'
      }`}
    >
      <div className="text-sm text-gray-500 truncate">
        <span className="hidden sm:inline">Welcome, </span>
        <span className="font-medium text-gray-900">{user?.full_name || user?.username}</span>
        <span className="ml-1.5 px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[10px] sm:text-xs font-medium">
          {user?.role}
        </span>
      </div>

      <div className="flex items-center gap-2 md:gap-3">
        {/* Availability status */}
        {user && <AvailabilityToggle userId={user.id} />}

        {/* Notifications */}
        <a
          href="/notifications"
          className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
        >
          <Bell size={18} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </a>

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <User size={18} />
          </button>
          {showUserMenu && (
            <div className="absolute right-0 top-10 w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
              <div className="px-3 py-2 border-b border-gray-100">
                <p className="text-sm font-medium">{user?.username}</p>
                <p className="text-xs text-gray-500">{user?.email || 'No email'}</p>
              </div>
              {/* My Phone Number for VoIP */}
              <div className="px-3 py-2 border-b border-gray-100">
                <p className="text-xs text-gray-500 mb-1 flex items-center gap-1"><Phone size={10} /> My Phone (for calls)</p>
                {editingPhone ? (
                  <div className="flex gap-1">
                    <input
                      type="tel"
                      value={myPhone}
                      onChange={(e) => setMyPhone(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                      placeholder="+91..."
                      autoFocus
                    />
                    <button onClick={savePhone} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check size={14} /></button>
                  </div>
                ) : (
                  <button
                    onClick={() => setEditingPhone(true)}
                    className="text-xs text-left w-full hover:text-indigo-600"
                  >
                    {myPhone || <span className="text-gray-400 italic">Set phone number</span>}
                    {phoneSaved && <span className="text-green-600 ml-1">Saved!</span>}
                  </button>
                )}
              </div>
              <button
                onClick={() => { logout(); setShowUserMenu(false) }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <LogOut size={14} />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
